/**
 * StatsClient - Fire-and-forget statistics tracking client
 *
 * A lightweight client for sending anonymous usage statistics to the Stats API.
 * All operations are non-blocking and errors are silently handled.
 *
 * @example
 * import StatsClient from './StatsClient.js';
 *
 * const stats = new StatsClient({
 *     toolName: 'calculator',
 *     toolVersion: '1.0.0',
 *     apiKey: 'your-api-key',
 *     apiUrl: 'https://matematica.ch/api/feedbackData.php'
 * });
 *
 * // Fire and forget - won't block your app
 * stats.sendStats({
 *     operation: 'addition',
 *     result_length: 5,
 *     has_error: false
 * });
 */
export default class StatsClient {
    /**
     * Create a new StatsClient instance
     *
     * @param {Object} config - Configuration object
     * @param {string} config.toolName - Name of the tool (must match API config)
     * @param {string} config.toolVersion - Semantic version (e.g., '1.0.0')
     * @param {string} config.apiKey - API key for authentication
     * @param {string} config.apiUrl - Full URL to API endpoint
     * @param {boolean} [config.debug=false] - Enable debug logging
     */
    constructor({ toolName, toolVersion, apiKey, apiUrl, debug = false }) {
        this.toolName = toolName;
        this.toolVersion = toolVersion;
        this.apiKey = apiKey;
        this.apiUrl = apiUrl;
        this.debug = debug;

        // Validate configuration
        if (!this.toolName || !this.toolVersion || !this.apiKey || !this.apiUrl) {
            throw new Error(
                'StatsClient: Missing required configuration (toolName, toolVersion, apiKey, apiUrl)'
            );
        }

        // Validate semantic versioning
        if (!/^[0-9]+\.[0-9]+\.[0-9]+(-[a-zA-Z0-9]+)?$/.test(this.toolVersion)) {
            throw new Error(
                `StatsClient: Invalid version format '${this.toolVersion}'. Expected semantic versioning (e.g., 1.0.0)`
            );
        }

        if (this.debug) {
            console.log('StatsClient initialized:', {
                toolName: this.toolName,
                toolVersion: this.toolVersion,
                apiUrl: this.apiUrl,
            });
        }
    }

    /**
     * Send statistics to the API (fire and forget)
     *
     * This method is non-blocking and will never throw errors.
     * All errors are silently caught and logged to console.
     *
     * @param {Object} payload - Key-value pairs of statistics to track
     * @returns {void}
     *
     * @example
     * stats.sendStats({
     *     operation: 'multiplication',
     *     result_length: 10,
     *     has_error: false
     * });
     */
    sendStats(payload) {
        // Validate payload
        if (!this._isValidPayload(payload)) {
            if (this.debug) {
                console.warn(
                    'StatsClient: Invalid payload (must be an object with key-value pairs)'
                );
            }
            return;
        }

        // Fire and forget - no await, no blocking
        this._sendRequest(payload);
    }

    /**
     * Validate that payload is a plain object (not array, null, or primitive)
     *
     * @private
     * @param {*} payload - Value to validate
     * @returns {boolean}
     */
    _isValidPayload(payload) {
        if (!payload || typeof payload !== 'object') {
            return false;
        }

        // Check if it's an array
        if (Array.isArray(payload)) {
            return false;
        }

        // Check if it has at least one key
        if (Object.keys(payload).length === 0) {
            return false;
        }

        return true;
    }

    /**
     * Send request to API (async but not awaited by caller)
     *
     * @private
     * @param {Object} payload - Statistics payload
     */
    async _sendRequest(payload) {
        try {
            const response = await fetch(this.apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': this.apiKey,
                },
                body: JSON.stringify({
                    // eslint-disable-next-line camelcase
                    tool_name: this.toolName,
                    // eslint-disable-next-line camelcase
                    tool_version: this.toolVersion,
                    payload: payload,
                }),
                // Don't send credentials/cookies for CORS requests
                credentials: 'omit',
            });

            // Parse response (only if JSON content-type)
            let result = null;
            const contentType = response.headers.get('Content-Type');
            if (contentType && contentType.includes('application/json')) {
                try {
                    result = await response.json();
                } catch (e) {
                    if (this.debug) {
                        console.warn('StatsClient: Failed to parse JSON response', e.message);
                    }
                }
            }

            if (this.debug) {
                if (!response.ok) {
                    console.warn(`StatsClient: API returned error (${response.status})`, result);
                }
            }
        } catch (error) {
            // Silently catch all errors - network failures, CORS issues, etc.
            if (this.debug) {
                console.warn('StatsClient: Failed to send stats', error.message);
            }
            // Don't rethrow - this should never break the application
        }
    }

    /**
     * Update tool version (useful if version changes during runtime)
     *
     * @param {string} newVersion - New semantic version string
     */
    setVersion(newVersion) {
        if (!/^[0-9]+\.[0-9]+\.[0-9]+(-[a-zA-Z0-9]+)?$/.test(newVersion)) {
            console.error(`StatsClient: Invalid version format '${newVersion}'`);
            return;
        }

        this.toolVersion = newVersion;

        if (this.debug) {
            console.log(`StatsClient: Version updated to ${newVersion}`);
        }
    }

    /**
     * Enable or disable debug logging
     *
     * @param {boolean} enabled - Whether to enable debug mode
     */
    setDebug(enabled) {
        this.debug = !!enabled;
    }
}
